import { useSyncExternalStore } from 'react';

// ---------------------------------------------------------------------------
// Ajuste (activado/volumen) del aviso sonoro global de pedido/recarga.
//
// El propio sondeo + reproducción vivía antes aquí (con dos ficheros .wav fijos del repo,
// independientes del Tracker en vivo). Se ha sustituido por GlobalLiveCelebration +
// useLiveCelebration, que usan los sonidos que suba el administrador desde Sonidos del Tracker
// (y ya no caen a ningún aviso por defecto si no hay ninguno subido) — ver
// src/hooks/live-activity/use-live-celebration.ts. Este archivo se queda solo con el ajuste
// (activado/volumen), que sigue siendo el mismo interruptor para el usuario: se lee desde el
// drawer de ajustes y desde el propio Tracker en vivo.
// ---------------------------------------------------------------------------
const STORAGE_KEY = 'notification-sounds-settings';

export type NotificationSoundSettings = {
  enabled: boolean;
  volume: number; // 0–100
};

const DEFAULT_SETTINGS: NotificationSoundSettings = { enabled: true, volume: 70 };

// Simple pub/sub so multiple consumers stay in sync
const listeners = new Set<() => void>();
function emitChange() {
  listeners.forEach((l) => l());
}

function getRawSettings(): NotificationSoundSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(next: Partial<NotificationSoundSettings>) {
  const current = getRawSettings();
  const merged = { ...current, ...next };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  emitChange();
}

// Snapshot for useSyncExternalStore (must return same ref when unchanged)
let cachedSnapshot = getRawSettings();
function getSnapshot(): NotificationSoundSettings {
  const fresh = getRawSettings();
  if (
    fresh.enabled !== cachedSnapshot.enabled ||
    fresh.volume !== cachedSnapshot.volume
  ) {
    cachedSnapshot = fresh;
  }
  return cachedSnapshot;
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

/** React hook — returns current settings + setters */
export function useNotificationSoundSettings() {
  const settings = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    ...settings,
    setEnabled: (enabled: boolean) => saveSettings({ enabled }),
    setVolume: (volume: number) => saveSettings({ volume }),
  };
}
